import { useState, useEffect, useMemo } from 'react'
import { createClient, type SupabaseClient } from '@/supabase'
import { isSupabaseEnvConfigured } from '@/supabase/supabase-client'
import { User } from '@supabase/supabase-js'
import { useRouter } from 'next/navigation'

/**
 * Supabase 클라이언트 및 인증 상태를 관리하는 공통 Hook
 * 
 * @returns {Object} Supabase 관련 상태 및 함수
 * - supabase: Supabase 브라우저 클라이언트 인스턴스
 * - user: 현재 로그인한 사용자 정보
 * - loading: 인증 상태 로딩 중 여부
 * - error: 인증 오류 (있는 경우)
 * - refreshUser: 사용자 정보 수동 새로고침 함수
 */
export const useSupabase = () => {
  const router = useRouter()
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  
  // useMemo로 supabase 클라이언트를 한 번만 생성
  const supabase = useMemo<SupabaseClient | null>(() => {
    if (typeof window === 'undefined') return null
    if (!isSupabaseEnvConfigured) {
      return null
    }
    try {
      return createClient()
    } catch (error) {
      console.error('[useSupabase] 클라이언트 생성 실패:', error)
      return null
    }
  }, [])

  // 사용자 정보 새로고침 함수
  const refreshUser = async () => {
    if (!supabase) return
    
    try {
      setLoading(true)
      setError(null)
      
      // getUser()를 사용하여 토큰 검증과 함께 사용자 정보 조회
      const { data: { user }, error: authError } = await supabase.auth.getUser()
      
      if (authError) {
        // 403 오류는 조용히 처리 (세션 정리는 loadUser에서 처리)
        if (authError.status === 403) {
          setUser(null)
          setLoading(false)
          return
        }
        setError(authError)
        setUser(null)
        return
      }
      
      setUser(user)
    } catch (err) {
      const error = err instanceof Error ? err : new Error('사용자 정보 조회 실패')
      setError(error)
      setUser(null)
      console.error('[useSupabase] 사용자 정보 조회 실패:', error)
    } finally {
      setLoading(false)
    }
  }

  // 초기 사용자 정보 로드 및 인증 상태 변경 감지
  useEffect(() => {
    // supabase가 없으면 무한 로딩을 피하기 위해 loading을 종료하고 에러 상태로 전환
    if (!supabase) {
      setLoading(false)
      setError(new Error('Supabase 클라이언트를 초기화할 수 없습니다. 환경변수를 확인해주세요.'))
      return
    }

    let mounted = true
    let lastLogTime = 0
    const LOG_THROTTLE_MS = 5000 // 5초마다 한 번만 로그 출력

    const loadUser = async () => {
      try {
        setLoading(true)
        setError(null)
        
        // getUser()를 사용하여 토큰 검증과 함께 사용자 정보 조회
        // 로그인 직후 쿠키가 아직 설정되지 않았을 수 있으므로, 재시도 로직 추가
        let user: User | null = null
        let authError: any = null
        let retryCount = 0
        const maxRetries = 2
        
        while (retryCount <= maxRetries) {
          const result = await supabase.auth.getUser()
          user = result.data.user
          authError = result.error
          
          // 사용자가 있으면 중단
          if (user) {
            break
          }
          
          // 마지막 시도가 아니고, 사용자가 없으면 잠시 대기 후 재시도
          if (retryCount < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, 300))
            retryCount++
          } else {
            break
          }
        }
        
        // 디버깅: 사용자 확인 로그 (개발 환경에서만)
        if (process.env.NODE_ENV !== 'production') {
          console.log('[useSupabase] 사용자 확인:', {
            hasUser: !!user,
            userId: user?.id,
            error: authError,
            retryCount,
            cookies: typeof document !== 'undefined' ? document.cookie : 'N/A'
          })
        }
        
        // 사용자가 없으면 세션도 없음
        if (!user) {
          // 사용자가 없는 것은 정상적인 상황(비로그인 상태)이므로 로그 출력하지 않음
          setUser(null)
          setLoading(false)
          return
        }
        
        if (!mounted) return
        
        // 403 Forbidden 또는 invalid claim 오류 처리
        if (authError) {
          const isInvalidToken = authError.status === 403 || 
                                authError.message?.includes('missing sub claim') ||
                                authError.message?.includes('invalid claim')
          
          if (isInvalidToken) {
            console.warn('[useSupabase] 인증 토큰이 유효하지 않습니다. (쿠키는 유지)', {
              status: authError.status,
              message: authError.message,
              note: 'signOut()을 호출하지 않으므로 쿠키는 그대로 유지됩니다.',
            })
            // ⚠️ 중요: 로그인 직후 잠깐 실패할 수 있으므로, signOut()을 호출하지 않음
            // signOut()을 호출하면 쿠키가 삭제되므로, 쿠키는 그대로 두고 사용자만 null로 설정
            // 다음 getUser() 호출 시 다시 확인하거나, onAuthStateChange가 처리할 것
            setUser(null)
            setError(null) // 403은 에러로 표시하지 않음
          } else {
            setError(authError)
            setUser(null)
          }
          setLoading(false)
          return
        }
        
        setUser(user)
      } catch (err) {
        if (!mounted) return
        const error = err instanceof Error ? err : new Error('사용자 정보 조회 실패')
        setError(error)
        setUser(null)
        console.error('[useSupabase] 사용자 정보 조회 실패:', error)
      } finally {
        if (mounted) {
          setLoading(false)
        }
      }
    }

    loadUser()

    // 인증 상태 변경 감지
    let lastAuthEventLogTime = 0
    const AUTH_EVENT_LOG_THROTTLE_MS = 5000 // 5초마다 한 번만 로그 출력
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted) return
      
      // 디버깅: 인증 상태 변경 로그 (개발 환경에서만)
      if (process.env.NODE_ENV !== 'production') {
        console.log('[useSupabase] 인증 상태 변경:', {
          event,
          hasSession: !!session,
          hasUser: !!session?.user,
          userId: session?.user?.id
        })
      }
      
      // 로그인/로그아웃 시 서버 컴포넌트 갱신 (App Router)
      if (event === 'SIGNED_IN' || event === 'SIGNED_OUT') {
        router.refresh()
      }
      
      // 모든 인증 이벤트에 대해 세션 상태 업데이트
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'INITIAL_SESSION') {
        // 세션이 있으면 사용자 정보 업데이트
        if (session?.user) {
          // 세션 토큰 검증 (로그 제한, SIGNED_IN일 때만)
          if (event === 'SIGNED_IN' && session?.access_token && process.env.NODE_ENV !== 'production') {
            const token = session.access_token
            if (token && typeof token === 'string') {
              try {
                // JWT 형식 확인 (header.payload.signature)
                const parts = token.split('.')
                if (parts.length === 3) {
                  try {
                    // Base64 URL-safe를 표준 Base64로 변환
                    let base64Payload = parts[1]
                    // JWT는 Base64 URL-safe 인코딩을 사용 (-, _ 사용, 패딩 없을 수 있음)
                    base64Payload = base64Payload.replace(/-/g, '+').replace(/_/g, '/')
                    
                    // 패딩 추가 (Base64는 4의 배수 길이여야 함)
                    while (base64Payload.length % 4) {
                      base64Payload += '='
                    }
                    
                    // Base64 디코딩
                    const decodedPayload = atob(base64Payload)
                    const payload = JSON.parse(decodedPayload)
                    if (!payload.sub) {
                      console.error('[useSupabase] ⚠️ SIGNED_IN 이벤트의 세션 토큰에 sub가 없습니다!')
                      // 로그인 직후 잠깐 실패할 수 있으므로, 바로 세션을 정리하지 않음
                      // 사용자만 null로 설정하고, 다음 이벤트에서 다시 확인
                      setUser(null)
                      return
                    }
                  } catch (parseError) {
                    // JSON 파싱 실패는 무시 (로그 제한)
                    const now = Date.now()
                    if (now - lastAuthEventLogTime > AUTH_EVENT_LOG_THROTTLE_MS) {
                      console.warn('[useSupabase] 토큰 payload 파싱 실패 (계속 진행):', parseError instanceof Error ? parseError.message : parseError)
                      lastAuthEventLogTime = now
                    }
                  }
                } else {
                  const now = Date.now()
                  if (now - lastAuthEventLogTime > AUTH_EVENT_LOG_THROTTLE_MS) {
                    console.warn('[useSupabase] 토큰이 JWT 형식이 아닙니다:', { partsLength: parts.length })
                    lastAuthEventLogTime = now
                  }
                }
              } catch (e) {
                // 토큰 파싱 실패는 경고만 하고 계속 진행 (로그 제한)
                const now = Date.now()
                if (now - lastAuthEventLogTime > AUTH_EVENT_LOG_THROTTLE_MS) {
                  console.warn('[useSupabase] 토큰 검증 실패 (계속 진행):', e instanceof Error ? e.message : e)
                  lastAuthEventLogTime = now
                }
              }
            }
          }
          setUser(session.user)
          setError(null)
        } else {
          // 세션이 없으면 사용자 정보 제거
          setUser(null)
          setError(null)
        }
      } else if (event === 'SIGNED_OUT') {
        setUser(null)
        setError(null)
      }
    })

    return () => {
      mounted = false
      subscription.unsubscribe()
    }
  }, [supabase])

  return {
    supabase: supabase as SupabaseClient, // 클라이언트에서만 사용되므로 타입 단언 사용
    user,
    loading,
    error,
    refreshUser,
  }
}
